'use client'

import { useAuth } from '@/lib/authContext'
import type { AuthModalContext } from '@/lib/authContext'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Modal } from '../ui/modal'
import Image from 'next/image'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  redirectPath?: string
  context?: AuthModalContext
}

function buildContent(context?: AuthModalContext) {
  const name = context?.name
  const type = context?.userType?.toLowerCase()

  if (!name) {
    return {
      headline: <>Join <span className="text-primary">Culineer</span> — it&apos;s free</>,
      sub: 'Create a free account to follow local farms and markets.',
      bullets: [
        "Always know what's fresh near you",
        'Find recipes for local ingredients',
        'Support local farmers and markets',
      ],
    }
  }

  if (type === 'market') {
    return {
      headline: <>Follow <span className="text-primary">{name}</span></>,
      sub: "Get notified about market days, seasonal vendors, and what's fresh — free.",
      bullets: [
        `Know exactly when ${name} is open`,
        "See what vendors are selling each week",
        'Save your favorite local markets',
      ],
    }
  }

  if (type === 'producer') {
    return {
      headline: <>Follow <span className="text-primary">{name}</span></>,
      sub: "Be the first to know when new products are available near you — free.",
      bullets: [
        `Get updates directly from ${name}`,
        'See what\'s in season and where to buy',
        'Support local growers in your community',
      ],
    }
  }

  return {
    headline: <>Follow <span className="text-primary">{name}</span></>,
    sub: 'Create a free account to connect with your local food community.',
    bullets: [
      "Always know what's fresh near you",
      'Find recipes for local ingredients',
      'Support local farmers and markets',
    ],
  }
}

const LoginModal = ({ isOpen, onClose, redirectPath, context }: LoginModalProps) => {
  const router = useRouter()
  const { hideAuthModal } = useAuth()

  const handleNavigation = (path: '/auth/login' | '/auth/join-culineer') => {
    router.push(
      `${path}?redirect=${encodeURIComponent(
        redirectPath || window.location.pathname
      )}`
    )
    hideAuthModal()
  }

  const handleLogin = () => handleNavigation('/auth/login')
  const handleSignup = () => handleNavigation('/auth/join-culineer')

  const { headline, sub, bullets } = buildContent(context)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl'>
      <div className="relative flex flex-col md:flex-row max-h-[85vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
          aria-label="Close"
        >
          ✕
        </button>
        {/* Left Side */}
        <div className="w-full md:w-1/2 flex flex-col justify-center px-6 py-8 md:px-10">
          <Link href={'/'} className='pb-4'>
            <img src="/assets/logo.svg" alt="Culineer" className="h-[45px] w-[134px]" />
          </Link>

          <h2 className="text-2xl font-bold mb-2 leading-snug break-words pr-6">{headline}</h2>
          <p className="text-sm text-gray-500 mb-5">{sub}</p>

          <ul className="space-y-3 text-gray-700 mb-6">
            {bullets.map((bullet) => (
              <li key={bullet} className="flex items-start text-sm">
                <CheckCircleIcon className='text-blue-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0' fill='rgb(63, 131, 248)' />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSignup}
              className="w-full bg-primary text-white px-6 py-2.5 rounded-full font-semibold shadow hover:bg-primary/80 transition text-center"
            >
              Join free — no credit card needed
            </button>
            <button
              onClick={handleLogin}
              className="w-full bg-[#E5F3D4] text-[#416922] px-6 py-2.5 rounded-full font-normal hover:bg-[#E5F3D4]/80 transition text-center"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>

        {/* Right Side - Image (hidden on small screens where there isn't room for it) */}
        <div className="hidden md:block md:w-1/2 shrink-0">
          <img
            src="/images/login-modal.png"
            alt="Farmers Market"
            className="h-full w-full rounded-r-2xl object-cover"
          />
        </div>
      </div>
    </Modal>
  )
}

export default LoginModal
